/**
 * EnquiryFormModal — Before-you-enquire mini-form.
 *
 * Gated moment: customer taps "Message supplier". We intercept and collect
 * a short qualification form so the supplier receives a ready-to-quote
 * lead (not a "hey, r u available?"). Pre-fills everything we know.
 *
 * Fields (minimum viable):
 *   - event_date (required, pre-filled)
 *   - postcode / location (pre-filled)
 *   - services_required (checkbox list, pre-ticks the current supplier's category)
 *   - start_time / end_time (optional)
 *   - budget_min / budget_max (preset chips)
 *   - customer_phone (optional — encouraged)
 *
 * Name + email come from the logged-in user (no need to retype).
 *
 * On submit: PUT /events/{id}/context → POST /chats with event_id → navigate.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Calendar, MapPin, Clock, CurrencyGbp, Phone, Check } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { id: 'dj', label: 'DJ / Band', emoji: '🎧' },
  { id: 'photographer', label: 'Photographer', emoji: '📷' },
  { id: 'videographer', label: 'Videographer', emoji: '🎥' },
  { id: 'photobooth', label: 'Photo Booth', emoji: '📸' },
  { id: 'caterer', label: 'Caterer', emoji: '🍽️' },
  { id: 'florist', label: 'Florist', emoji: '🌼' },
  { id: 'venue', label: 'Venue', emoji: '🏛️' },
  { id: 'cake_maker', label: 'Cake Maker', emoji: '🎂' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'decorator', label: 'Decorator', emoji: '🎨' },
  { id: 'wedding_planner', label: 'Wedding Planner', emoji: '✨' },
  { id: 'hair_makeup', label: 'Hair & Makeup', emoji: '💅' },
];

const BUDGET_PRESETS = [
  { min: 0, max: 300, label: 'Under £300' },
  { min: 300, max: 600, label: '£300 – £600' },
  { min: 600, max: 1200, label: '£600 – £1.2k' },
  { min: 1200, max: 2500, label: '£1.2k – £2.5k' },
  { min: 2500, max: 10000, label: '£2.5k+' },
];

const EnquiryFormModal = ({ supplier, onClose, onSubmitted }) => {
  const [step, setStep] = useState('loading'); // loading | need_event | form | submitting
  const [myEvents, setMyEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [form, setForm] = useState({
    event_date: '',
    event_postcode: '',
    services_required: supplier?.category ? [supplier.category] : [],
    start_time: '',
    end_time: '',
    budget_min: null,
    budget_max: null,
    customer_phone: '',
  });

  // Load user's events + prefer ?event_id= param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlEventId = urlParams.get('event_id');
    axios.get(`${API}/users/me/events`).then(async (r) => {
      const events = r.data.events || [];
      setMyEvents(events);
      if (!events.length) { setStep('need_event'); return; }
      const initialId = urlEventId && events.find((e) => e.event_id === urlEventId)
        ? urlEventId : events[0].event_id;
      setSelectedEventId(initialId);
      await hydrateFromEvent(initialId);
      setStep('form');
    }).catch(() => setStep('need_event'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hydrateFromEvent = async (eventId) => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/context`);
      const ctx = data.event_context || {};
      setForm((prev) => ({
        ...prev,
        event_date: ctx.event_date || prev.event_date,
        event_postcode: ctx.event_postcode || prev.event_postcode,
        services_required: (ctx.services_required && ctx.services_required.length)
          ? ctx.services_required
          : (supplier?.category && !prev.services_required.length ? [supplier.category] : prev.services_required),
        start_time: ctx.start_time || prev.start_time,
        end_time: ctx.end_time || prev.end_time,
        budget_min: ctx.budget_min ?? prev.budget_min,
        budget_max: ctx.budget_max ?? prev.budget_max,
        customer_phone: ctx.customer_phone || prev.customer_phone,
      }));
    } catch { /* silent — form stays pre-filled with supplier category */ }
  };

  const onPickEvent = async (eventId) => {
    setSelectedEventId(eventId);
    await hydrateFromEvent(eventId);
  };

  const toggleService = (id) => {
    setForm((prev) => {
      const has = prev.services_required.includes(id);
      return {
        ...prev,
        services_required: has
          ? prev.services_required.filter((s) => s !== id)
          : [...prev.services_required, id],
      };
    });
  };

  const pickBudget = (preset) => {
    setForm((prev) => ({ ...prev, budget_min: preset.min, budget_max: preset.max }));
  };

  const submit = async () => {
    if (!selectedEventId) {
      toast.error('Pick an event first');
      return;
    }
    if (!form.event_date) {
      toast.error('Please set an event date');
      return;
    }
    if (!form.services_required.length) {
      toast.error('Pick at least one service');
      return;
    }
    setStep('submitting');
    try {
      // 1. Update event context with enquiry details
      await axios.put(`${API}/events/${selectedEventId}/context`, {
        event_date: form.event_date,
        event_postcode: form.event_postcode,
        services_required: form.services_required,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        budget_min: form.budget_min,
        budget_max: form.budget_max,
        customer_phone: form.customer_phone || null,
      });
      // 2. Open chat
      const { data } = await axios.post(`${API}/chats`, {
        supplier_id: supplier.id, event_id: selectedEventId,
      });
      onSubmitted?.(data.chat_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start enquiry');
      setStep('form');
    }
  };

  const onBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose?.();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
      onClick={onBackdrop}
      data-testid="enquiry-form-modal"
    >
      <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-white/5">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Before you enquire</p>
            <h3 className="font-black text-lg leading-tight mt-0.5">
              A couple quick details so {supplier?.business_name || 'they'} can give you an accurate quote
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white -mr-2 -mt-1 p-2"
            data-testid="enquiry-close-btn"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {step === 'loading' && (
            <p className="text-white/40 text-sm text-center py-6">Loading your event…</p>
          )}

          {step === 'need_event' && (
            <div className="text-center py-6" data-testid="enquiry-need-event">
              <p className="font-bold mb-1">You'll need an event first</p>
              <p className="text-white/60 text-xs mb-4">Create an event to share context with suppliers — takes 30 seconds.</p>
              <button
                onClick={() => { window.location.href = '/create'; }}
                className="py-2.5 px-5 rounded-lg text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="enquiry-create-event-btn"
              >
                Create event
              </button>
            </div>
          )}

          {(step === 'form' || step === 'submitting') && (
            <>
              {/* Event picker (only if user has >1 events) */}
              {myEvents.length > 1 && (
                <div data-testid="enquiry-event-picker">
                  <label className="block text-[11px] font-black uppercase tracking-wider text-white/60 mb-1.5">Enquiring for</label>
                  <select
                    value={selectedEventId || ''}
                    onChange={(e) => onPickEvent(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#B89762]/40/40"
                  >
                    {myEvents.map((e) => (
                      <option key={e.event_id} value={e.event_id} className="bg-[#15161B]">
                        {e.event_name} {e.event_date ? `· ${e.event_date}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Date */}
              <Field icon={<Calendar size={14} weight="fill" />} label="Event date">
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="flex-1 bg-transparent text-sm text-white focus:outline-none"
                  data-testid="enquiry-date-input"
                  required
                />
              </Field>

              {/* Postcode */}
              <Field icon={<MapPin size={14} weight="fill" />} label="Location / postcode">
                <input
                  type="text"
                  placeholder="SW1A 1AA or Manchester"
                  value={form.event_postcode}
                  onChange={(e) => setForm({ ...form, event_postcode: e.target.value })}
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                  data-testid="enquiry-postcode-input"
                />
              </Field>

              {/* Times */}
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-1.5">
                  <Clock size={12} weight="fill" /> Times (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="enquiry-start-time"
                  />
                  <span className="text-white/40 self-center text-xs">to</span>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="enquiry-end-time"
                  />
                </div>
              </div>

              {/* Services required */}
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-white/60 mb-1.5">Services needed</label>
                <div className="flex flex-wrap gap-1.5" data-testid="enquiry-services">
                  {SERVICE_OPTIONS.map((opt) => {
                    const on = form.services_required.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleService(opt.id)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
                          on
                            ? 'bg-[#B89762]/30/15 text-[#F4E9D2] border-[#B89762]/40/40'
                            : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
                        }`}
                        data-testid={`enquiry-service-${opt.id}`}
                      >
                        {on && <Check size={10} weight="bold" className="inline mr-1 -mt-0.5" />}
                        {opt.emoji} {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-1.5">
                  <CurrencyGbp size={12} weight="fill" /> Budget
                </label>
                <div className="flex flex-wrap gap-1.5" data-testid="enquiry-budget">
                  {BUDGET_PRESETS.map((p) => {
                    const on = form.budget_min === p.min && form.budget_max === p.max;
                    return (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => pickBudget(p)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
                          on
                            ? 'bg-[#B89762]/30/15 text-[#F4E9D2] border-[#B89762]/40/40'
                            : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
                        }`}
                        data-testid={`enquiry-budget-${p.min}`}
                      >
                        {on && <Check size={10} weight="bold" className="inline mr-1 -mt-0.5" />}
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phone (optional, encouraged) */}
              <Field icon={<Phone size={14} weight="fill" />} label="Phone (optional — only Studio Pass suppliers can see it)">
                <input
                  type="tel"
                  placeholder="+44 7700 900000"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                  data-testid="enquiry-phone-input"
                />
              </Field>
            </>
          )}
        </div>

        {step === 'form' || step === 'submitting' ? (
          <div className="border-t border-white/5 p-4">
            <button
              onClick={submit}
              disabled={step === 'submitting'}
              className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="enquiry-submit-btn"
            >
              {step === 'submitting' ? 'Sending enquiry…' : 'Send enquiry'}
            </button>
            <p className="text-white/40 text-[10px] text-center mt-2">
              Takes suppliers ~12 mins to reply on average.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Field = ({ icon, label, children }) => (
  <div>
    <label className="block text-[11px] font-black uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-1.5">
      {icon} {label}
    </label>
    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
      {children}
    </div>
  </div>
);

export default EnquiryFormModal;
